import { JobRecord } from "common/records/Job";
import { TokenKind, paymentProviders } from "common/records/Token";
import { RootState } from "common/reducers";
import { ids } from "common/selectors/idsManagement.selectors";
import { image } from "common/selectors/image.selectors";
import { currentJob, hasIntegration } from "common/selectors/job.selectors";
import { currentOrgId, currentOrgPrefConfig } from "common/selectors/org.selectors";
import { Nullable } from "common/types";
import { List } from "immutable";
import { createSelector } from "reselect";

/**
 * Get the authByOrgId state
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, AuthorizedTokensRecord>} Map<number, AuthorizedTokensRecord>
 */
export const authByOrgId = (state: RootState) => state.getIn(["tokens", "authByOrgId"]);

/**
 * Get the byOrgId state
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, TokenRecord>} Map<number, TokenRecord>
 */
export const byOrgId = (state: RootState) => state.getIn(["tokens", "byOrgId"]);

/**
 * Get the lastSavedByOrgId state
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, TokenRecord>} Map<number, TokenRecord>
 */
export const lastSavedByOrgId = (state: RootState) => state.getIn(["tokens", "lastSavedByOrgId"]);

/**
 * Get the authorized tokens
 *
 * @param {RootState} state The RootState
 * @param {{ orgId: number }} options
 * @returns {AuthorizedTokensRecord} AuthorizedTokensRecord
 */
export const authorizedTokens = (state: RootState, props: { orgId: Nullable<number> }) => {
  if (!props.orgId) return null;

  return authByOrgId(state).get(props.orgId);
};

/**
 * Wait on authorized tokens to be defined and loaded
 *
 * @param {RootState} state The RootState
 * @param {{ orgId: number }} options
 * @returns {boolean} boolean
 */
export const waitOnAuthorizedTokens = (state: RootState, props: { orgId: Nullable<number> }) => {
  const authTokens = authorizedTokens(state, props);
  if (!authTokens) return false;

  return !authTokens.get("loading");
};

/**
 * Get the authorized token
 *
 * @param {RootState} state The RootState
 * @param {{ orgId: number, kind: TokenKind }} options
 * @returns {AuthorizedTokenRecord} AuthorizedTokenRecord
 */
export const authorizedToken = (state: RootState, props: { orgId: Nullable<number>; kind: Nullable<TokenKind> }) => {
  if (!props.kind) return null;

  return authorizedTokens(state, { orgId: props.orgId })?.get("byKind")?.get(props.kind);
};

/**
 * Loads whether an integration is authorized
 *
 * @param {RootState} state The RootState
 * @param {{ orgId: Nullable<number>; kind: Nullable<TokenKind> }} props
 * @returns {boolean} Indicates if the integration has been authorized.
 */
export const getAuthorization = (state: RootState, props: { orgId: Nullable<number>; kind: Nullable<TokenKind> }) => {
  return authorizedToken(state, props)?.get("authorized") || false;
};

/**
 * Get the authorized token for current org (no need to pass orgId)
 *
 * @param {RootState} state The RootState
 * @param {{ kind: TokenKind }} options
 * @returns {AuthorizedTokenRecord} AuthorizedTokenRecord
 */
export const currentOrgAuthorizedToken = (state: RootState, props: { kind: TokenKind }) => {
  const orgId = currentOrgId(state);
  return authorizedToken(state, { orgId, kind: props.kind });
};

/**
 * Get the authorized tokens for current org (no need to pass orgId)
 *
 * @param {RootState} state The RootState
 * @returns {AuthorizedTokensRecord} AuthorizedTokensRecord
 */
export const currentOrgAuthorizedTokens = (state: RootState) => {
  const orgId = currentOrgId(state);
  return authorizedTokens(state, { orgId });
};

/**
 * Get the allIntegrations state from idsManagement
 *
 * @param {RootState} state The RootState
 * @returns {List<TokenKind>} List<TokenKind>
 */
export const allIntegrations = (state: RootState) => ids(state, { modelName: "allIntegrations" }) as List<TokenKind>;

/**
 * Get the categoryIntegrations state from idsManagement
 *
 * @param {RootState} state The RootState
 * @returns {List<TokenKind>} List<TokenKind>
 */
export const categoryIntegrations = (state: RootState) => ids(state, { modelName: "integrations" }) as List<TokenKind>;

/**
 * Loads last saved token by kind and current org
 *
 * @param {RootState} state The RootState
 * @param {{ kind: string }} options
 * @returns {TokenRecord} TokenRecord
 */
export const lastSavedToken = (state: RootState, props: { kind: TokenKind }) => {
  const orgId = currentOrgId(state);
  if (!orgId) return null;

  return lastSavedByOrgId(state).get(orgId)?.get(props.kind) || null;
};

/**
 * Loads token by kind and current org
 *
 * @param {RootState} state The RootState
 * @param {{ kind: string }} options
 * @returns {TokenRecord} TokenRecord
 */
export const token = (state: RootState, props: { kind: TokenKind }) => {
  const orgId = currentOrgId(state);
  if (!orgId) return null;

  return byOrgId(state).get(orgId)?.get(props.kind) || null;
};

/**
 * Loads token.data by kind and current org
 *
 * @param {RootState} state The RootState
 * @param {{ kind: string }} options
 * @returns {Map<any, any>} Map<any, any>
 */
export const tokenData = (state: RootState, props: { kind: TokenKind }) => {
  return token(state, props)?.get("data");
};

/**
 * Loads token.playload by kind and current org
 *
 * @param {RootState} state The RootState
 * @param {{ kind: string }} options
 * @returns {Map<any, any>} Map<any, any>
 */
export const tokenPayload = (state: RootState, props: { kind: TokenKind }) => {
  return token(state, props)?.get("payload");
};

/**
 * Loads loading status by kind and current org
 *
 * @param {RootState} state The RootState
 * @param {{ kind: string }} options
 * @returns {boolean} loading
 */
export const loading = (state: RootState, props: { kind: TokenKind }): boolean =>
  byOrgId(state)
    .get(currentOrgId(state) as number)
    ?.get(props.kind)
    ?.get("loading") || false;

/**
 * Checks if there is an active payment provider
 *
 * @param {RootState} state The RootState
 * @param {{ kind: string }} options
 * @returns {boolean} boolean
 */
export const paymentProviderActive = (state: RootState) => {
  return paymentProviders.some(
    (provider) => authorizedToken(state, { orgId: currentOrgId(state), kind: provider })?.authorized || false,
  );
};

/**
 * Returns true or false based on whether token.data or org.preferences.config has attachment preferences set or not
 *
 * @param {RootState} state The RootState
 * @param {kind: TokenKind} kind
 * @returns {boolean} true or false
 */
export const hasAttachmentPreference = (
  state: RootState,
  integration: TokenKind,
  preferenceKey: string,
  preferenceValue: string,
) => {
  return (
    token(state, { kind: integration })
      ?.data?.get("attachment_preferences")
      ?.get(preferenceKey)
      ?.includes(preferenceValue) ||
    currentOrgPrefConfig(state, { path: ["marketsharp", preferenceKey] })?.includes(preferenceValue) ||
    false
  );
};

/**
 * Returns true or false based on whether the job has token integration values or not.
 *
 * @param {RootState} state - The RootState
 * @param {string[]} integrationKeys - An array of integration keys
 * @returns {boolean} true or false
 */
export const isIntegrated = (state: RootState, integrationKeys: string[]): boolean => {
  const job = currentJob(state);

  if (!job) return false;

  return integrationKeys.some((integrationKey: string) => {
    const isDirectlyStored = job?.metadata?.get(integrationKey);
    if (isDirectlyStored) return true;

    return hasIntegration(job as JobRecord, integrationKey);
  });
};

/**
 * Returns true or false based on whether token.valid_token value
 *
 * @param {RootState} state The RootState
 * @param {kind: TokenKind} kind
 * @returns {boolean} true or false
 */
export const isTokenValid = createSelector([token], (token) => {
  return token?.valid_token || false;
});

/**
 * Loads token.image_ids by kind and current org
 *
 * @param {RootState} state The RootState
 * @param {{ kind: string }} options
 * @returns {List<number>} List<number>
 */
export const tokenImageIds = (state: RootState, props: { kind: TokenKind }) => {
  return token(state, props)?.get("image_ids") || List();
};

/**
 * Loads token.images by kind and current org
 *
 * @param {RootState} state The RootState
 * @param {{ kind: string }} options
 * @returns {List<ImageRecord>} List<ImageRecord>
 */
export const tokenImages = (state: RootState, props: { kind: TokenKind }) => {
  return tokenImageIds(state, props).map((imageId) => image(state, { imageId }));
};

/**
 * Returns true if improveit360 has platform lightning
 *
 * @param {RootState} state The RootState
 * @returns {Boolean} Boolean
 */
export const isI360Lightning = (state: RootState) => {
  return tokenData(state, { kind: "improveit360" })?.get("platform") === "lightning";
};

/**
 * Get Salesforce job mapping values (integration metadata fields)
 * Extracts field names from pull_data_config.job_mapping that start with "metadata.integration."
 *
 * @param {RootState} state The RootState
 * @returns {List<string>} List of field names
 */
export const salesforceJobMappingValues = (state: RootState) => {
  const salesforceToken = token(state, { kind: "salesforce" });
  if (!salesforceToken) {
    return List<string>();
  }
  const jobMapping = salesforceToken.getIn(["data", "pull_data_config", "job_mapping"], List());
  return jobMapping
    .map((value: any) => value.get("occ_value"))
    .filter((value: string) => value?.startsWith("metadata.integration."))
    .map((value: string) => value.split(".").pop());
};

/**
 * Get Salesforce job mapping root values (root metadata fields)
 * Extracts field names from pull_data_config.job_mapping that start with "metadata." but NOT "metadata.integration."
 *
 * @param {RootState} state The RootState
 * @returns {List<string>} List of field names
 */
export const salesforceJobMappingRootValues = (state: RootState) => {
  const salesforceToken = token(state, { kind: "salesforce" });
  if (!salesforceToken) {
    return List<string>();
  }
  const jobMapping = salesforceToken.getIn(["data", "pull_data_config", "job_mapping"], List());
  return jobMapping
    .map((value: any) => value.get("occ_value"))
    .filter((value: string) => value?.startsWith("metadata.") && !value?.startsWith("metadata.integration."))
    .map((value: string) => value.split(".").pop());
};
